import { Order } from '@@types'
import { ORDER } from '@hive/status'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'

import { useMutateCancelOrder } from '@entities/Order/api/cancelOrder.gql'

import { useModalStore } from '@features/modal'

import useOrderRatingModal from '@widgets/order-rating-modal/hooks/useOrderRatingModal'
import ReceiptsModal from '@widgets/receipts-modal'

import Button from '@shared/ui/button'
import { useToastStore } from '@shared/ui/toast/useToastStore'

import CancelOrderModal from './CancelOrderModal'

import styles from './styles.module.scss'

type OrderFooterProps = {
  order: Order
  onRatingSuccess?: () => void
  onCancel?: () => void
}

export default function OrderFooter ({ order, onRatingSuccess, onCancel }: OrderFooterProps) {
  const { t: to } = useTranslation('order')
  const { openModal } = useModalStore()
  const { addToast } = useToastStore()
  const [ cancelOrder ] = useMutateCancelOrder()
  
  const { openOrderRatingModal } = useOrderRatingModal({
    order,
    onSuccess: onRatingSuccess,
  })

  const showRateButton =
    order.status === ORDER.DELIVERED &&
    !order.reviewInvite &&
    order?.feedbacks?.length === 0

  const showReceiptsButton = order?.receipts && order?.receipts?.length > 0
  const showCancelButton = order.status === ORDER.CONFIRMED

  const handleCancelOrder = async () => {
    try {
      const { data } = await cancelOrder({
        variables: {
          order_id: order.id,
        },
      })

      if (data?.cancelOrder.status === -1) {
        addToast({
          message: to('order.cancel.success', { id: order.id }),
          variant: 'error',
        })
        onCancel?.()
      }
    }
    catch (error: unknown) {
      addToast({
        message: (error as Error)?.message || to('order.cancel.error'),
        variant: 'error',
      })
    }
  }

  const handleCancelClick = () => {
    openModal('cancelOrder', <CancelOrderModal onConfirm={handleCancelOrder} />, {
      title: to('order.cancel.title'),
    })
  }

  if (!showRateButton && !showReceiptsButton && !showCancelButton) {
    return null
  }

  return (
    <div className={styles.footer}>
      <div className={styles.footer__buttons}>
        {showRateButton && (
          <Button
            className={styles.footer__rateButton}
            onClick={openOrderRatingModal}
            theme='primary'
          >
            {to('order.button.rate')}
          </Button>
        )}
        
        {showReceiptsButton && (
          <button
            onClick={() => {
              openModal('receipts', <ReceiptsModal order={order} />, {
                title: to('order.button.receipt'),
              })
            }}
            className={styles.footer__receipt}
          >
            <ReactSVG src="/icons/receipt.svg" />
            <span>{to('order.button.receipt')}</span>
          </button>
        )}
        
        {showCancelButton && (
          <Button
            className={styles.footer__cancelButton}
            onClick={handleCancelClick}
            theme='grey'
          >
            {to('order.button.cancel')}
          </Button>
        )}
      </div>
    </div>
  )
}